/**
 * The per-turn army draft.
 *
 * Returned as an itemised breakdown rather than a bare number, because the
 * whole point is that the table can see *why* it is nine and stop recounting.
 */

import type { ContinentId, SeatId, TerritoryId } from '../ids.js';
import { continentsHeldBy, territoriesHeldBy, type MapIndex } from './adjacency.js';
import type { RulesConfig } from './config.js';

export interface ContinentAward {
  readonly continent: ContinentId;
  readonly bonus: number;
}

export interface ReinforcementBreakdown {
  readonly territoryCount: number;
  /** `max(minimum, floor(territories / divisor))`. */
  readonly fromTerritories: number;
  readonly continents: readonly ContinentAward[];
  readonly fromContinents: number;
  readonly fromCapitals: number;
  /** Armies from card sets traded this turn, passed in by the caller. */
  readonly fromCards: number;
  readonly total: number;
}

export interface ReinforcementInput {
  readonly index: MapIndex;
  readonly seat: SeatId;
  readonly ownerOf: ReadonlyMap<TerritoryId, SeatId | null>;
  readonly rules: RulesConfig;
  readonly fromCards?: number;
  readonly capitalsHeld?: number;
}

export function computeReinforcements(input: ReinforcementInput): ReinforcementBreakdown {
  const { index, seat, ownerOf, rules } = input;
  const owned = territoriesHeldBy(index, seat, ownerOf);
  const territoryCount = owned.length;

  // A player with no territories is out; they draft nothing, minimum or not.
  const fromTerritories =
    territoryCount === 0
      ? 0
      : Math.max(rules.reinforcement.minimum, Math.floor(territoryCount / rules.reinforcement.divisor));

  const continents = continentsHeldBy(index, seat, ownerOf).map((continent) => ({
    continent,
    bonus: bonusFor(continent, index, rules),
  }));
  const fromContinents = continents.reduce((acc, c) => acc + c.bonus, 0);

  const fromCapitals = (input.capitalsHeld ?? 0) * rules.reinforcement.capitalBonus;
  const fromCards = input.fromCards ?? 0;

  return {
    territoryCount,
    fromTerritories,
    continents,
    fromContinents,
    fromCapitals,
    fromCards,
    total: fromTerritories + fromContinents + fromCapitals + fromCards,
  };
}

/** The map's bonus, unless the table has overridden it in the rules config. */
function bonusFor(continent: ContinentId, index: MapIndex, rules: RulesConfig): number {
  const override = rules.reinforcement.continentBonusOverrides[continent];
  return override ?? index.continents.get(continent)?.bonus ?? 0;
}
